/**
 * "Tipo de exame" (E126.F2, GUS-105, ADR-0088 §2.1; E165.F1.T03, GUS-135, HTML 6.8): um card com a
 * grade de três colunas do molde — "Contrato Digital", "Contrato Não Digital" e "Análise morfológica
 * de fotografia" —, como botões de escolha única. A morfológica aparece e não se escolhe: o laudo
 * próprio ainda não foi habilitado (GUS-114), então ela fica desabilitada, com "Indisponível por
 * enquanto".
 *
 * E176.F1 (GUS-252, ADR-0129): o banco escolhe os mesmos serviços que o escritório. O selo "Pelo
 * escritório" da E143.F2 saiu: uma regra comercial por cliente, se vier, sai da configuração dele.
 */
import { ptBR } from '@/i18n/pt-BR';
import { SERVICOS_DA_ABERTURA, type ServicoDaAbertura } from '@/lib/abertura';
import { cn } from '@/lib/utils';

import { CartaoDaAbertura } from './PecasDaAbertura';

const t = ptBR.abertura.servico;

const CONTRATAVEIS = SERVICOS_DA_ABERTURA.flatMap((o) =>
  o.contratavel && o.servico !== 'morfologica' ? [o.servico] : [],
);
const MORFOLOGICA_INDISPONIVEL = SERVICOS_DA_ABERTURA.some(
  (o) => o.servico === 'morfologica' && !o.contratavel,
);

const OPCAO =
  'flex min-h-[112px] flex-col items-start justify-center gap-1 bg-bg p-[18px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

export interface CartoesDeServicoProps {
  escolhido: ServicoDaAbertura;
  onEscolher: (servico: ServicoDaAbertura) => void;
  disabled?: boolean;
}

function OpcaoDeServico({
  servico,
  selecionado,
  disabled,
  onEscolher,
}: {
  servico: 'eletronica' | 'grafotecnica';
  selecionado: boolean;
  disabled: boolean;
  onEscolher: () => void;
}): JSX.Element {
  const descricao = t[servico].descricao;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selecionado}
      aria-disabled={disabled}
      data-testid={`abertura-servico-${servico}`}
      onClick={() => {
        if (!disabled) onEscolher();
      }}
      className={cn(
        OPCAO,
        selecionado ? 'border-2 border-accent-700 p-[17px]' : 'border border-border',
        'hover:border-accent',
      )}
    >
      <span className="block font-sans text-[17px] font-semibold leading-tight text-fg">
        {t[servico].nome}
      </span>
      {descricao !== '' ? (
        <span className="block text-[14px] text-fg-muted">{descricao}</span>
      ) : null}
    </button>
  );
}

export function CartoesDeServico({
  escolhido,
  onEscolher,
  disabled = false,
}: CartoesDeServicoProps): JSX.Element {
  return (
    <CartaoDaAbertura titulo={t.pergunta} testId="abertura-servico">
      <div
        role="radiogroup"
        aria-label={t.pergunta}
        className="grid grid-cols-1 gap-3 md:grid-cols-3"
        data-testid="abertura-servicos"
      >
        {CONTRATAVEIS.map((servico) => (
          <OpcaoDeServico
            key={servico}
            servico={servico}
            selecionado={servico === escolhido}
            disabled={disabled}
            onEscolher={() => onEscolher(servico)}
          />
        ))}
        {MORFOLOGICA_INDISPONIVEL ? (
          <button
            type="button"
            role="radio"
            aria-checked={false}
            disabled
            aria-disabled
            aria-label={t.morfologica.ariaLabel}
            data-testid="abertura-servico-morfologica"
            className={cn(OPCAO, 'cursor-not-allowed border border-border opacity-[0.55]')}
          >
            <span className="block font-sans text-[17px] font-semibold leading-tight text-fg">
              {t.morfologica.nome}
            </span>
            <span className="block text-[14px] text-fg-muted">{t.morfologica.indisponivel}</span>
          </button>
        ) : null}
      </div>
    </CartaoDaAbertura>
  );
}
